import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Award, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { Spinner, EmptyState, StatusBadge } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { api, errMsg, fmtDate } from "../../lib/api";
import { StaffLabel } from "../../components/StaffAvatar";
import { ReviewDialog } from "../admin/Approvals";
import { CertificateChip, CertificateDownloadButton, CertificateVerifyLink } from "../../components/TransferCertificate";  // Phase 124

/**
 * Phase 124 — approved ownership transfers with their certificates. Supervisors see the tenant register,
 * collectors only the transfers they recorded (server-side scoping on /transfer-cases).
 */
export function TransferCertificatesSection({ code, limit = 10 }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/t/${code}/transfer-cases`, { params: { status: "approved", page: 1, limit } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [code, limit]);
  if (!items || items.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="transfer-certificates-section">
      <p className="text-sm font-medium flex items-center gap-2"><Award className="h-4 w-4 text-primary" /> Ownership transfer certificates (recent)</p>
      {items.map((t) => (
        <Card key={t.id} className="shadow-sm" data-testid={`transfer-certificate-card-${t.transfer_id}`}>
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="font-mono text-sm font-medium">{t.transfer_id} <span className="font-sans text-xs text-muted-foreground">• {t.pin}</span></p>
              <p className="text-sm truncate">{t.old_owner_name} <span className="text-muted-foreground">→</span> <span className="font-medium">{t.new_owner_name}</span></p>
              <p className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                <CertificateChip certificateNumber={t.certificate_number} testId={`transfer-certificate-chip-${t.transfer_id}`} />
                <span>approved {fmtDate(t.reviewed_at || t.updated_at)}</span>
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <CertificateVerifyLink code={code} token={t.certificate_token} testId={`transfer-certificate-verify-${t.transfer_id}`} />
              <CertificateDownloadButton code={code} caseRef={t.id} certificateNumber={t.certificate_number}
                testId={`transfer-certificate-download-${t.transfer_id}`} />
              <StatusBadge status={t.status} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Workflow approvals routed to the logged-in approver (supervisor / collector with rights).
export default function AssignedApprovals() {
  const { code } = useParams();
  const [data, setData] = useState(null); // {reassessments, surveys, pending}
  const [taxHeads, setTaxHeads] = useState([]);
  const [fys, setFys] = useState([]);
  const [review, setReview] = useState(null); // {item, type}

  const load = useCallback(() => api.get(`/t/${code}/approvals/assigned`).then((r) => setData(r.data)), [code]);

  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
    api.get(`/t/${code}/masters/tax-heads`).then((r) => setTaxHeads(r.data)).catch(() => {});
    api.get(`/t/${code}/masters/financial-years`).then((r) => setFys(r.data)).catch(() => {});
  }, [load, code]);

  if (!data) return <Spinner />;

  const Item = ({ it, type }) => (
    <Card className="shadow-sm">
      <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="font-mono text-sm font-medium">{it.reassessment_id || it.survey_id}</p>
          <p className="text-sm truncate">{it.owner_name || it.pin}{it.reason ? ` • ${it.reason}` : ""}</p>
          <p className="text-xs text-muted-foreground">By <StaffLabel code={code} userId={it.initiated_by || it.surveyed_by} name={it.initiated_by_name || it.surveyed_by_name} empCode={it.initiated_by_employee_id || it.surveyed_by_employee_id} /> • {fmtDate(it.created_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={it.status} />
          <Button size="sm" onClick={() => setReview({ item: it, type })} data-testid={`assigned-review-${it.id}`}>Review</Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4" data-testid="assigned-approvals-page">
      <div>
        <h1 className="text-lg font-semibold font-heading flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5" /> Workflow Approvals
        </h1>
        <p className="text-sm text-muted-foreground">
          {data.pending} case{data.pending === 1 ? "" : "s"} routed to you for a decision
        </p>
      </div>

      {data.pending === 0 ? (
        <EmptyState title="No approvals waiting" subtitle="Cases sent to you from field reassessments and surveys appear here." />
      ) : (
        <div className="space-y-4">
          {data.reassessments.length > 0 && (
            <div className="space-y-2" data-testid="assigned-reassessments">
              <p className="text-sm font-medium">Reassessments ({data.reassessments.length})</p>
              {data.reassessments.map((it) => <Item key={it.id} it={it} type="reassessment" />)}
            </div>
          )}
          {data.surveys.length > 0 && (
            <div className="space-y-2" data-testid="assigned-surveys">
              <p className="text-sm font-medium">New Surveys ({data.surveys.length})</p>
              {data.surveys.map((it) => <Item key={it.id} it={it} type="survey" />)}
            </div>
          )}
        </div>
      )}

      <TransferCertificatesSection code={code} />

      {review && (
        <ReviewDialog code={code} item={review.item} type={review.type} taxHeads={taxHeads} fys={fys}
          onDone={() => { setReview(null); load(); }} />
      )}
    </div>
  );
}
